import type { IMultiEntityLinkDataSource } from "@/data/dao/service/BaseServiceDao";
import type { ILinkableEntity } from "@/data/model/Shared";
import { Format } from "@/data/util/Format";

import type { IMultiEntityInputConfig } from "@/components/formControls/MultiEntityInput";

import { i18n } from "@/portal/Trans";

export interface IEntityInputConfig {
    dataSource: IMultiEntityLinkDataSource;
    takenIds?: Array<string>;
}

export class EntityInput {
    private $element: JQuery;
    private $input: JQuery;
    private $search: JQuery;

    private selectedId?: string;
    private selectedEntity?: ILinkableEntity;

    private runningSearchXhr?: Promise<any>;
    private searchTypeDelay?: number;
    private closeDelay?: number;

    private config: IEntityInputConfig;

    constructor(config: IEntityInputConfig, $input?: JQuery) {
        this.$element = $('<div class="entityinput" />');

        this.config = config;

        if ($input === undefined)
            this.$input = $(
                '<input type="text" name="mi" class="form-control" placeholder="' + i18n("general.searchTerm") + '" />'
            ).appendTo(this.$element);
        else {
            const parent = $input.attr("placeholder", i18n("general.searchTerm")).parent();
            this.$input = $input.detach().appendTo(this.$element);
            this.renderTo(parent);
        }

        this.$input
            .focus(() => this.openSearch())
            .blur(() => this.closeSearch())
            .keyup(() => {
                if (this.searchTypeDelay !== undefined) clearTimeout(this.searchTypeDelay);

                this.searchTypeDelay = window.setTimeout(() => {
                    this.updateSearch(this.$input.val().length === 0 ? undefined : this.$input.val());
                    this.searchTypeDelay = undefined;
                }, 320);
            });

        this.$search = $('<div class="search" />').appendTo(this.$element);
    }

    public getValue(): string | undefined {
        return this.selectedId;
    }

    public setValue(value?: string | ILinkableEntity): EntityInput {
        this.selectedId = undefined;
        this.selectedEntity = undefined;

        this.$element.find("> .link").remove();

        if (value !== undefined) {
            if (typeof value === "string") this.selectedId = value;
            else this.add(value);
        }

        this.checkInputVisibility();

        return this;
    }

    public destroy() {
        this.$element.remove();
    }

    private openSearch() {
        if (this.selectedId !== undefined) return;

        this.$search
            .stop(true, true)
            .css("top", this.$element.height() + 3)
            .slideDown("fast", "easeOutExpo")[0].offsetTop;
        this.$element.addClass("open");

        this.updateSearch(undefined);

        if (this.closeDelay !== undefined) {
            clearTimeout(this.closeDelay);
            (this as any).closeSearch = undefined;
        }
    }

    private checkInputVisibility() {
        const isVisible = this.$input.is(":visible");
        if (isVisible && this.selectedId !== undefined) this.$input.hide();
        else if (!isVisible && this.selectedId === undefined) this.$input.show();
    }

    private closeSearch(ignoreDelay?: boolean) {
        // if (this.runningSearchXhr !== undefined && !this.runningSearchXhr.isFulfilled) {
        //     this.runningSearchXhr.cancel();
        //     this.runningSearchXhr = undefined;
        // }

        if (this.closeDelay !== undefined) clearTimeout(this.closeDelay);

        if (!this.$element.hasClass("open")) return;

        this.closeDelay = window.setTimeout(
            () => {
                this.$input.val("");
                this.$search.stop(true, false).slideUp("fast", "easeOutExpo", () => {
                    this.$search.removeAttr("style");
                })[0].offsetTop;
                this.$element.removeClass("open");
                this.closeDelay = undefined;
            },
            ignoreDelay !== undefined && ignoreDelay === true ? 0 : 120
        );
    }

    private updateSearch(term?: string) {
        // if (this.runningSearchXhr !== undefined && !this.runningSearchXhr.isFulfilled) {
        //     this.runningSearchXhr.cancel();
        //     this.runningSearchXhr = undefined;
        // }

        const selectedIds = this.config.takenIds !== undefined ? [...this.config.takenIds] : [];
        if (this.selectedId !== undefined) selectedIds.push(this.selectedId);

        this.$search
            .empty()
            .append(
                '<div class="loading"><div class="kt-spinner kt-spinner--sm kt-spinner--success"></div> ' +
                    i18n("general.searchingResults") +
                    "</div>"
            );
        this.runningSearchXhr = this.config.dataSource.search(selectedIds, term).then((e) => {
            this.$search.empty();

            if (e.length === 0) {
                this.$search.append('<div class="nodata">' + i18n("general.noSearchResults") + "</div>");
                return;
            }

            for (const i in e) {
                const searchableEntry: ILinkableEntity = e[i];

                $('<div class="option">' + Format.htmlEscape(searchableEntry.name) + "</div>")
                    .data("model", searchableEntry)
                    .appendTo(this.$search)
                    .click((e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        this.add($(e.currentTarget).data("model"));
                        this.$input.val("");

                        if (this.closeDelay !== undefined) {
                            clearTimeout(this.closeDelay);
                            this.closeDelay = undefined;
                        }

                        this.checkInputVisibility();
                        this.closeSearch(true);
                    });
            }
        });
    }

    private add(entity: ILinkableEntity) {
        if (this.selectedId === entity.id) return;

        this.selectedId = entity.id;
        this.selectedEntity = entity;

        const link = $('<div class="link">' + Format.htmlEscape(entity.name) + "</div>")
            .data("model", entity)
            .insertBefore(this.$input);
        $('<div class="remover"><i class="flaticon2-cross"></i></div>')
            .appendTo(link)
            .click((e) => {
                const cl = $(e.currentTarget).parent(".link");

                this.selectedEntity = undefined;
                this.selectedId = undefined;

                cl.remove();
                this.checkInputVisibility();
            });
    }

    public setError(hasError: boolean): EntityInput {
        if (hasError) this.$element.addClass("is-invalid");
        else this.$element.removeClass("is-invalid");

        return this;
    }

    public setDisabled(isDisabled: boolean): EntityInput {
        this.$input.prop("disabled", isDisabled);

        return this;
    }

    public renderTo($element: JQuery): EntityInput {
        this.$element.appendTo($element);
        return this;
    }

    public static createFromInput($input: JQuery, config: IMultiEntityInputConfig): EntityInput {
        return new EntityInput(config, $input);
    }
}
